// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Grid from "@insite/mobius/Icons/Grid";
import List from "@insite/mobius/Icons/List";
import Table from "@insite/mobius/Icons/Table";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { getSystemColor, xClassNames } from "@sc/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => ({
    loaded: !!getProductListDataView(state).value,
    view: state.pages.productList.view,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    setView,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListViewSelectStyles {
    wrapper?: InjectableCss;
    listViewClickable?: ClickablePresentationProps;
    listViewIcon?: IconPresentationProps;
    gridViewClickable?: ClickablePresentationProps;
    gridViewIcon?: IconPresentationProps;
    tableViewClickable?: ClickablePresentationProps;
    tableViewIcon?: IconPresentationProps;
    // XNG-Changes
    title?: TypographyPresentationProps;
}

export const viewSelectStyles: ProductListViewSelectStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding-top: 5px;
            float: right;
        `,
    },
    listViewClickable: {
        css: css`
            margin-right: 20px;
        `,
    },
    gridViewClickable: {
        css: css`
            margin-right: 20px;
        `,
    },
    listViewIcon: {
        src: List,
        size: 30,
    },
    gridViewIcon: {
        src: Grid,
        size: 30,
    },
    tableViewIcon: {
        src: Table,
        size: 26,
    },
};

const styles = viewSelectStyles;

class ProductListViewSelect extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const view = getCookie("ProductList-View");
        // XNG-Changes: add isTabletScreen
        if (view && this.props.view !== view && !this.props.isTabletScreen) {
            this.props.setView({ view: view as ProductListViewType });
        }
    }

    render() {
        const { loaded, view, setView } = this.props;

        if (!loaded) {
            return null;
        }

        const clickListViewHandler = () => {
            changeView("List");
        };

        const clickGridViewHandler = () => {
            changeView("Grid");
        };

        const clickTableViewHandler = () => {
            changeView("Table");
        };

        const changeView = (view: ProductListViewType) => {
            setCookie("ProductList-View", view);
            setView({ view });
        };

        return (
            <StyledWrapper {...styles.wrapper}>
                <Clickable
                    data-test-selector="viewSelectList"
                    {...styles.listViewClickable}
                    onClick={clickListViewHandler}
                    aria-label="Product List View"
                    className={xClassNames.append([view === "List" ? "selected" : ""])}
                >
                    <VisuallyHidden>{translate("Product List View")}</VisuallyHidden>
                    <Icon {...styles.listViewIcon} color={view === "List" ? getSystemColor("common.ui1") : "primary"} />
                </Clickable>
                <Clickable
                    data-test-selector="viewSelectGrid"
                    {...styles.gridViewClickable}
                    onClick={clickGridViewHandler}
                    aria-label="Product Grid View"
                    className={xClassNames.append([view === "Grid" ? "selected" : ""])}
                >
                    <VisuallyHidden>{translate("Product Grid View")}</VisuallyHidden>
                    <Icon {...styles.gridViewIcon} color={view === "Grid" ? getSystemColor("common.ui1") : "primary"} />
                </Clickable>
                {/* XNG-Changes */}
                {/* <Clickable
                    data-test-selector="viewSelectTable"
                    {...styles.gridViewClickable}
                    onClick={clickTableViewHandler}
                >
                    <VisuallyHidden>{translate("Product Table View")}</VisuallyHidden>
                    <Icon {...styles.tableViewIcon} color={view === "Table" ? "primary" : undefined} />
                </Clickable> */}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListViewSelect),
    definition: {
        group: "Product List",
        displayName: "View Select",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
